import { AcrossPayload, AffiliateData, AffiliateStruct, BridgeType, MetaData } from '@/types'

import { ethers } from 'ethers'
import { set } from 'lodash-es'

export const IS_ACROSS_REFUND_FAIL = !!new URLSearchParams(window.location.search).get('acrossRefundFail')

const AFFILIATE_SCHEMA = {
  format: 'json',
  encoding: 'abi.encode',
  schema: {
    provider: 'string',
  },
} as const

const getBridgeSchema = (bridgeType: BridgeType) => {
  const baseSchema = {
    format: 'json',
    encoding: 'abi.encode',
    schema: {
      provider: 'string',
      bridgingProtocol: 'string', // 'across' for now
      metadata: {
        fromTokenId: 'string',
        toTokenId: 'string',
        fromAddress: 'string',
        toAddress: 'string',
        fromNetworkId: 'string', // chainId or token
        fromToken: 'string', // address of token
      },
    },
  }
  if (IS_ACROSS_REFUND_FAIL) {
    set(baseSchema, 'schema.acrossRefundFail', 'bool')
  }
  if (bridgeType === 'BTC-EVM') {
    return {
      ...baseSchema,
      schema: {
        ...baseSchema.schema,
        acrossPayload: {
          toToken: 'string',
          toChain: 'uint256',
          message: 'string',
          recipient: 'address',
          maxAcrossFee: 'uint256',
          depositor: 'address',
          maxRetryTimeout: 'uint256',
        },
      },
    }
  }
  return baseSchema
}

export const getAffiliateData = (acrossPayload?: AcrossPayload, metadata?: MetaData): AffiliateData => {
  const baseData: AffiliateData = {
    provider: 'optimex',
  }
  if (acrossPayload) {
    baseData.bridgingProtocol = 'across'
    baseData.acrossPayload = acrossPayload
  }
  if (metadata) {
    baseData.bridgingProtocol = 'across'
    baseData.metadata = metadata
  }

  return baseData
}

const toSolidityType = (type: any): string => {
  if (typeof type === 'string') return type
  if (Array.isArray(type)) {
    const struct = type[0]
    const members = Object.entries(struct)
      .map(([key, val]) => `${toSolidityType(val)} ${key}`)
      .join(',')
    return `tuple(${members})[]`
  }
  if (typeof type === 'object' && type !== null && !Array.isArray(type)) {
    const members = Object.entries(type)
      .map(([key, val]) => `${toSolidityType(val)} ${key}`)
      .join(',')
    return `tuple(${members})`
  }
  throw new Error(`Unsupported type: ${type}`)
}

export const schemaToAbi = (schema: Record<string, any>) => {
  const abiArgs = Object.entries(schema)
    .map(([key, val]) => `${toSolidityType(val)} ${key}`)
    .join(',')

  const abi = [`tuple(${abiArgs})`]
  return abi
}

export function encodeAffiliateData(inputData: AffiliateData, bridgeType?: BridgeType): AffiliateStruct {
  const abiCoder = new ethers.AbiCoder()

  const { acrossPayload, metadata } = inputData
  if (bridgeType === 'BTC-EVM') {
    const abi = schemaToAbi(getBridgeSchema(bridgeType).schema)
    const encoded = abiCoder.encode(abi, [
      [
        inputData.provider,
        inputData.bridgingProtocol,
        [
          metadata?.fromTokenId,
          metadata?.toTokenId,
          metadata?.fromAddress,
          metadata?.toAddress,
          metadata?.fromNetworkId,
          metadata?.fromToken,
        ],
        ...(IS_ACROSS_REFUND_FAIL ? [IS_ACROSS_REFUND_FAIL] : []),
        [
          acrossPayload?.toToken,
          acrossPayload?.toChain,
          acrossPayload?.message,
          acrossPayload?.recipient,
          acrossPayload?.maxAcrossFee,
          acrossPayload?.depositor,
          acrossPayload?.maxRetryTimeout,
        ],
      ],
    ])
    return {
      data: encoded,
      schema: JSON.stringify(getBridgeSchema(bridgeType)),
      aggregatedValue: '0',
    }
  } else if (bridgeType === 'EVM-BTC') {
    const abi = schemaToAbi(getBridgeSchema(bridgeType).schema)
    const encoded = abiCoder.encode(abi, [
      [
        inputData.provider,
        inputData.bridgingProtocol,
        [
          metadata?.fromTokenId,
          metadata?.toTokenId,
          metadata?.fromAddress,
          metadata?.toAddress,
          metadata?.fromNetworkId,
          metadata?.fromToken,
        ],
        ...(IS_ACROSS_REFUND_FAIL ? [IS_ACROSS_REFUND_FAIL] : []),
      ],
    ])
    return {
      data: encoded,
      schema: JSON.stringify(getBridgeSchema(bridgeType)),
      aggregatedValue: '0',
    }
  }

  const abi = schemaToAbi(AFFILIATE_SCHEMA.schema)
  const encoded = abiCoder.encode(abi, [[inputData.provider]])

  return {
    data: encoded,
    schema: JSON.stringify(AFFILIATE_SCHEMA),
    aggregatedValue: '0',
  }
}
